import React, { useState, useEffect, useMemo } from 'react';
import { useMandantStore } from '../../../store/mandantStore';
import { loadSetting, saveSetting } from '../../../services/settingsService';
import { WIETZ_TATBESTAENDE } from '../data/tatbestaendeWietz';

const KEY = 'wietz_faelligkeiten_v1';

// ── Typen ─────────────────────────────────────────────────────────────────

type WietzStatus = 'OFFEN' | 'ERLEDIGT' | 'UEBERFAELLIG';

interface WietzFaelligkeit {
  id:                 string;
  mandantId:          string;
  mitarbeiterKennung: string;  // MA-001 — kein Klarname
  tatbestandCode:     string;
  faelligkeitDatum:   string;
  letztesDatum?:      string;
  status:             WietzStatus;
  erstelltAm:         string;
}

// ── Hilfsfunktionen ───────────────────────────────────────────────────────

const today = (): string => new Date().toISOString().slice(0, 10);

function escalate(list: WietzFaelligkeit[]): WietzFaelligkeit[] {
  const t = today();
  return list.map(f =>
    f.status === 'OFFEN' && f.faelligkeitDatum < t
      ? { ...f, status: 'UEBERFAELLIG' as WietzStatus }
      : f,
  );
}

async function load(mandantId?: string): Promise<WietzFaelligkeit[]> {
  const all = await loadSetting<WietzFaelligkeit[]>(KEY, []);
  const esc = escalate(all);
  return mandantId ? esc.filter(f => f.mandantId === mandantId) : esc;
}

async function save(list: WietzFaelligkeit[]): Promise<void> {
  // load full list, merge by id
  const all = await loadSetting<WietzFaelligkeit[]>(KEY, []);
  const ids = new Set(list.map(f => f.id));
  const others = all.filter(f => !ids.has(f.id));
  await saveSetting(KEY, [...others, ...list]);
}

async function nextKennung(mandantId: string): Promise<string> {
  const all = await loadSetting<WietzFaelligkeit[]>(KEY, []);
  const nums = all
    .filter(f => f.mandantId === mandantId)
    .map(f => parseInt(f.mitarbeiterKennung.replace('MA-', ''), 10))
    .filter(n => !isNaN(n));
  const next = nums.length === 0 ? 1 : Math.max(...nums) + 1;
  return `MA-${String(next).padStart(3, '0')}`;
}

// ── Status-Badge ──────────────────────────────────────────────────────────

const STATUS_STYLE: Record<WietzStatus, string> = {
  OFFEN:        'bg-blue-100   text-blue-800   dark:bg-blue-950/30   dark:text-blue-300',
  ERLEDIGT:     'bg-green-100  text-green-800  dark:bg-green-950/30  dark:text-green-300',
  UEBERFAELLIG: 'bg-red-100    text-red-800    dark:bg-red-950/30    dark:text-red-300',
};

// ── Neuer-Eintrag-Formular ────────────────────────────────────────────────

function NeuerEintragForm({ mandantId, onAdd }: {
  mandantId: string;
  onAdd: (f: WietzFaelligkeit) => void;
}): React.JSX.Element {
  const [kennung,    setKennung]    = useState('');
  const [code,       setCode]       = useState(WIETZ_TATBESTAENDE[0]?.code ?? '');
  const [faellig,    setFaellig]    = useState('');
  const [letzte,     setLetzte]     = useState('');
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (!mandantId) { return; }
    setLoading(true);
    void nextKennung(mandantId).then(k => { setKennung(k); setLoading(false); });
  }, [mandantId]);

  const handleAdd = (): void => {
    if (!faellig || !code || !kennung) { return; }
    const t = today();
    const status: WietzStatus = faellig < t ? 'UEBERFAELLIG' : 'OFFEN';
    onAdd({
      id:                 crypto.randomUUID(),
      mandantId,
      mitarbeiterKennung: kennung,
      tatbestandCode:     code,
      faelligkeitDatum:   faellig,
      ...(letzte ? { letztesDatum: letzte } : {}),
      status,
      erstelltAm:         t,
    });
    setFaellig('');
    setLetzte('');
    void nextKennung(mandantId).then(setKennung);
  };

  return (
    <div className="card space-y-3">
      <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Neuer Überwachungseintrag</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="space-y-1">
          <label className="label-text">MA-Kennung</label>
          <input type="text" value={loading ? '…' : kennung}
            onChange={e => { setKennung(e.target.value); }}
            className="input-field font-mono text-sm" placeholder="MA-001" />
        </div>
        <div className="space-y-1 sm:col-span-1">
          <label className="label-text">WIETZ-Tatbestand</label>
          <select value={code} onChange={e => { setCode(e.target.value); }} className="input-field text-sm">
            {WIETZ_TATBESTAENDE.map(t => (
              <option key={t.code} value={t.code}>{t.code} – {t.bezeichnung.slice(0, 35)}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="label-text">Letzte Unters.</label>
          <input type="date" value={letzte} onChange={e => { setLetzte(e.target.value); }}
            className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Fällig am <span className="text-red-500">*</span></label>
          <input type="date" value={faellig} onChange={e => { setFaellig(e.target.value); }}
            className="input-field" />
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={handleAdd}
          disabled={!faellig || !code || !kennung}
          className="btn-primary text-sm disabled:opacity-50">
          + Eintrag hinzufügen
        </button>
      </div>
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────

type Filter = WietzStatus | 'ALLE';

export function WietzUeberwachung(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const [liste,  setListe]  = useState<WietzFaelligkeit[]>([]);
  const [filter, setFilter] = useState<Filter>('ALLE');

  useEffect(() => {
    void load(mandant?.id).then(setListe);
  }, [mandant?.id]);

  const persist = (updated: WietzFaelligkeit[]): void => {
    setListe(updated);
    void save(updated);
  };

  const handleAdd = (f: WietzFaelligkeit): void => {
    persist([...liste, f]);
  };

  const [erledigungId,    setErledigungId]    = useState<string | null>(null);
  const [erledigungDatum, setErledigungDatum] = useState<string>('');

  const handleErledigungConfirm = (id: string): void => {
    const datum = erledigungDatum || today();
    const all = liste.map(f => f.id === id
      ? { ...f, status: 'ERLEDIGT' as WietzStatus, letztesDatum: datum }
      : f,
    );
    // Auto-Folgetermin
    const orig = liste.find(f => f.id === id);
    if (orig !== undefined) {
      const tb = WIETZ_TATBESTAENDE.find(t => t.code === orig.tatbestandCode);
      if (tb !== undefined) {
        const basis = new Date(datum);
        basis.setMonth(basis.getMonth() + tb.intervallMon);
        all.push({
          id:                 crypto.randomUUID(),
          mandantId:          orig.mandantId,
          mitarbeiterKennung: orig.mitarbeiterKennung,
          tatbestandCode:     orig.tatbestandCode,
          faelligkeitDatum:   basis.toISOString().slice(0, 10),
          letztesDatum:       datum,
          status:             'OFFEN',
          erstelltAm:         today(),
        });
      }
    }
    persist(all);
    setErledigungId(null);
    setErledigungDatum('');
  };

  const handleDelete = (id: string): void => {
    persist(liste.filter(f => f.id !== id));
  };

  const filtered = useMemo(() =>
    filter === 'ALLE' ? liste : liste.filter(f => f.status === filter)
  , [liste, filter]);

  const counts: Record<WietzStatus, number> = useMemo(() => ({
    OFFEN:        liste.filter(f => f.status === 'OFFEN').length,
    UEBERFAELLIG: liste.filter(f => f.status === 'UEBERFAELLIG').length,
    ERLEDIGT:     liste.filter(f => f.status === 'ERLEDIGT').length,
  }), [liste]);

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <p className="text-xs text-gray-500">Offen</p>
          <p className={`text-2xl font-bold mt-1 ${counts.OFFEN > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
            {counts.OFFEN}
          </p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Überfällig</p>
          <p className={`text-2xl font-bold mt-1 ${counts.UEBERFAELLIG > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {counts.UEBERFAELLIG}
          </p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Erledigt</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
            {counts.ERLEDIGT}
          </p>
        </div>
      </div>

      {/* Neuer Eintrag */}
      {mandant !== null && mandant !== undefined
        ? <NeuerEintragForm mandantId={mandant.id} onAdd={handleAdd} />
        : (
          <div className="card bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm py-3 text-center">
            Bitte zuerst einen Mandanten auswählen.
          </div>
        )
      }

      {/* Filter */}
      <div className="flex flex-wrap gap-1.5">
        {(['ALLE', 'OFFEN', 'UEBERFAELLIG', 'ERLEDIGT'] as const).map(s => (
          <button key={s}
            onClick={() => { setFilter(s); }}
            className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
              ${filter === s
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
              }`}>
            {s === 'ALLE' ? `Alle (${liste.length})` : `${s} (${counts[s]})`}
          </button>
        ))}
      </div>

      {/* Tabelle */}
      {filtered.length === 0 ? (
        <div className="card text-center py-10 text-gray-400">
          <p className="text-3xl mb-2">📅</p>
          <p className="font-medium">
            {liste.length === 0
              ? 'Noch keine Einträge — oben Mandant wählen und ersten Eintrag anlegen.'
              : 'Keine Einträge für diesen Filter.'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Kennung</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3 hidden md:table-cell">Tatbestand</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3 hidden lg:table-cell">Letzte U.</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3">Fällig</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3">Status</th>
                <th className="w-20 px-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map(f => {
                const tb = WIETZ_TATBESTAENDE.find(t => t.code === f.tatbestandCode);
                return (
                  <tr key={f.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors
                    ${f.status === 'ERLEDIGT' ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-bold bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                        {f.mitarbeiterKennung}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{f.tatbestandCode}</p>
                      {tb !== undefined && (
                        <p className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">{tb.bezeichnung}</p>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-400 hidden lg:table-cell">
                      {f.letztesDatum ?? '—'}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {f.faelligkeitDatum}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[f.status]}`}>
                        {f.status}
                      </span>
                    </td>
                    <td className="px-2 py-3">
                      {erledigungId === f.id ? (
                        <div className="flex items-center gap-1">
                          <input type="date" value={erledigungDatum}
                            onChange={e => { setErledigungDatum(e.target.value); }}
                            className="input-field text-xs py-0.5 px-1 w-32" />
                          <button onClick={() => { handleErledigungConfirm(f.id); }}
                            className="text-xs bg-green-600 text-white px-2 py-0.5 rounded hover:bg-green-700">
                            OK
                          </button>
                          <button onClick={() => { setErledigungId(null); setErledigungDatum(''); }}
                            className="text-xs text-gray-400 hover:text-gray-600 px-1">
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          {f.status !== 'ERLEDIGT' && (
                            <button
                              onClick={() => { setErledigungId(f.id); setErledigungDatum(today()); }}
                              title="Als erledigt markieren + Folgetermin"
                              className="text-green-600 hover:text-green-800 text-base px-1">
                              ✓
                            </button>
                          )}
                          <button onClick={() => { handleDelete(f.id); }}
                            title="Löschen"
                            className="text-gray-300 hover:text-red-500 text-base px-1">
                            🗑
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 text-center">
        WIETZ-Überwachung gem. §§ 49–52 ASchG, AMR 4 · Pseudonymisiert · Dexie-Persistenz
      </p>
    </div>
  );
}
